/**
 * Appwrite Storage Service Adapter
 *
 * Implements IStorageService interface using Appwrite Storage API
 */

import { Client, Storage, ID, Permission, Role } from "appwrite";
import { IStorageService } from "../interfaces/IStorageService";
import { backendProvider } from "..";
import { getTeamLogoFilename } from "../../../utils/companyLogo";

/**
 * Appwrite Storage Bucket IDs
 */
export interface StorageBucketIds {
  profilePictures: string; // 69404ac3003660cd911d - profile-pictures
  logos: string; // 6940e3f8003c17bb7fc9 - brainiacs-logos
  workforceSignatures: string; // 6940e6c3002eddae9d6b - workforce-image-signature
  csvUploads: string; // csv-upload-folder - CSV/XLS/XLSX campaign files
  leadReports: string; // lead-reports - Lead PDF reports
  campaignsDocs?: string; // campaigns-docs - Campaign research PDF reports
  clientLogos: string; // client-logos - customer company branding
}

export class AppwriteStorageService implements IStorageService {
  private client: Client;
  private storage: Storage;
  private buckets: StorageBucketIds;

  constructor(client: Client, buckets: StorageBucketIds) {
    this.client = client;
    this.storage = new Storage(client);
    this.buckets = buckets;
  }

  /**
   * Map file path to appropriate bucket ID
   * Routes files to correct bucket based on path prefix:
   * - profile-pictures/* → profile-pictures bucket
   * - brainiacs-logos/* or logos/* → brainiacs-logos bucket
   * - workforce-image-signature/* or workforce/* → workforce-image-signature bucket
   * - lead-reports/* → lead-reports bucket
   * - csv-uploads/* → csv-uploads bucket
   * - Default → profile-pictures bucket
   */
  private getBucketId(path: string): string {
    const lowerPath = path.toLowerCase();

    // Lead reports bucket (PDF reports generated by n8n)
    if (lowerPath.startsWith("lead-reports/")) {
      return this.buckets.leadReports;
    }

    // Profile pictures bucket (default)
    if (
      lowerPath.startsWith("profile-pictures/") ||
      lowerPath.startsWith("profilepictures/") ||
      lowerPath.startsWith("profile_pictures/")
    ) {
      return this.buckets.profilePictures;
    }

    // Brainiacs logos bucket
    if (
      lowerPath.startsWith("brainiacs-logos/") ||
      lowerPath.startsWith("logos/") ||
      lowerPath.startsWith("company-logos/")
    ) {
      return this.buckets.logos;
    }

    // Workforce signatures bucket
    if (
      lowerPath.startsWith("workforce-image-signature/") ||
      lowerPath.startsWith("workforce/") ||
      lowerPath.startsWith("agents/") ||
      lowerPath.startsWith("team/")
    ) {
      return this.buckets.workforceSignatures;
    }

    // CSV uploads bucket (campaign lead files)
    if (
      lowerPath.startsWith("csv-uploads/") ||
      lowerPath.startsWith("csv/")
    ) {
      return this.buckets.csvUploads;
    }

    // Campaign docs bucket (research PDF reports)
    if (
      lowerPath.startsWith("campaigns-docs/") ||
      lowerPath.startsWith("campaign-docs/")
    ) {
      return this.buckets.campaignsDocs || 'campaigns-docs';
    }

    // Default to profile pictures bucket for backward compatibility
    console.warn(
      `⚠️ Path "${path}" doesn't match any bucket prefix, using profile-pictures bucket`,
    );
    return this.buckets.profilePictures;
  }

  async uploadFile(path: string, file: File | Blob): Promise<string> {
    try {
      const bucketId = this.getBucketId(path);
      // Deterministic ID for per-owner paths (e.g. profile-pictures/{userId}):
      // the LAST path segment is stable per owner, so re-uploads REPLACE the
      // previous file instead of accumulating duplicates in the bucket.
      // Paths that embed uniqueness (CSV campaign files, reports) fall back
      // to a random ID.
      const lastSegment = path.split("/").filter(Boolean).pop() || "";
      const isDeterministicPath = /^(profile-pictures|profilepictures|profile_pictures)\/[^/]+$/i.test(path);
      const fileId = isDeterministicPath && lastSegment ? lastSegment : ID.unique();

      let fileToUpload: File;
      if (file instanceof File) {
        fileToUpload = file;
      } else {
        const filename = `${fileId}.jpg`;
        fileToUpload = new File([file], filename, {
          type: file.type || "image/jpeg",
        });
      }

      // Get the current user
      const user = backendProvider.auth.getCurrentUser();
      if (!user) {
        throw new Error("User must be authenticated to upload files.");
      }

      // Set permissions for the user
      const permissions = [
        Permission.read(Role.user(user.uid)),
        Permission.update(Role.user(user.uid)),
        Permission.delete(Role.user(user.uid)),
      ];

      // Appwrite rejects createFile when the ID already exists (409), so a
      // deterministic ID means: create, and on conflict remove the previous
      // file then re-create. The brief URL break between delete and create
      // only affects the owner's own avatar for one request.
      let result;
      try {
        result = await this.storage.createFile(
          bucketId,
          fileId,
          fileToUpload,
          permissions,
        );
      } catch (createError: any) {
        if (createError?.code !== 409 || !isDeterministicPath) throw createError;
        console.log(`♻️ Replacing existing file ${fileId} in ${bucketId}`);
        await this.storage.deleteFile(bucketId, fileId).catch(() => undefined);
        result = await this.storage.createFile(
          bucketId,
          fileId,
          fileToUpload,
          permissions,
        );
      }

      const downloadURL = this.storage
        .getFileView(bucketId, result.$id)
        .toString();

      console.log(
        `✅ File uploaded to Appwrite Storage (bucket: ${bucketId}): ${path}`,
      );
      return downloadURL;
    } catch (error: any) {
      console.error(`❌ Appwrite uploadFile error (${path}):`, error);
      throw new Error(`Failed to upload file: ${error.message}`);
    }
  }

  async uploadTeamLogo(teamId: string, file: File): Promise<{ fileId: string; url: string }> {
    try {
      // Deterministic file ID = the team ID itself. Re-uploads OVERWRITE the
      // same Storage object instead of piling up orphaned duplicates in the
      // client-logos bucket. The visible filename keeps the Team ID too, so
      // n8n workflows can locate the client's logo without a mapping.
      const fileId = teamId;
      const normalizedFile = new File([file], getTeamLogoFilename(teamId, file.name), {
        type: file.type,
        lastModified: file.lastModified,
      });
      const teamRole = Role.team(teamId);

      // Deterministic ID → 409 when the team already has a logo: remove the
      // previous file and re-create, so the bucket holds ONE logo per team.
      let result;
      try {
        result = await this.storage.createFile({
          bucketId: this.buckets.clientLogos,
          fileId,
          file: normalizedFile,
          permissions: [
            Permission.read(teamRole),
          ],
        });
      } catch (createError: any) {
        if (createError?.code !== 409) throw createError;
        console.log(`♻️ Replacing existing team logo ${fileId}`);
        await this.storage.deleteFile({
          bucketId: this.buckets.clientLogos,
          fileId,
        }).catch(() => undefined);
        result = await this.storage.createFile({
          bucketId: this.buckets.clientLogos,
          fileId,
          file: normalizedFile,
          permissions: [
            Permission.read(teamRole),
          ],
        });
      }

      return {
        fileId: result.$id,
        url: this.storage.getFileView({
          bucketId: this.buckets.clientLogos,
          fileId: result.$id,
        }).toString(),
      };
    } catch (error: any) {
      console.error('❌ Appwrite team logo upload error:', error);
      if (error?.code === 403) {
        throw new Error('Your account does not have permission to upload to the client logo bucket.');
      }
      if (error?.code === 404) {
        throw new Error('The Appwrite client-logos bucket could not be found.');
      }
      throw new Error(`Failed to upload the company logo: ${error?.message || 'Unknown Appwrite error'}`);
    }
  }

  getTeamLogoViewUrl(fileId: string): string {
    return this.storage.getFileView({
      bucketId: this.buckets.clientLogos,
      fileId,
    }).toString();
  }

  async deleteTeamLogo(fileId: string): Promise<void> {
    try {
      await this.storage.deleteFile({ bucketId: this.buckets.clientLogos, fileId });
    } catch (error: any) {
      if (error?.code === 404) return;
      console.error(`❌ Appwrite team logo delete error (${fileId}):`, error);
      throw new Error(`Failed to delete the previous company logo: ${error?.message || 'Unknown Appwrite error'}`);
    }
  }

  async uploadBytes(path: string, bytes: Uint8Array): Promise<string> {
    try {
      const blob = new Blob([bytes]);
      return await this.uploadFile(path, blob);
    } catch (error: any) {
      console.error(`❌ Appwrite uploadBytes error (${path}):`, error);
      throw new Error(`Failed to upload bytes: ${error.message}`);
    }
  }

  async getDownloadURL(path: string): Promise<string> {
    try {
      const bucketId = this.getBucketId(path);
      const fileId = path.replace(/\//g, "_");
      const downloadURL = this.storage
        .getFileDownload(bucketId, fileId)
        .toString();
      return downloadURL;
    } catch (error: any) {
      console.error(`❌ Appwrite getDownloadURL error (${path}):`, error);
      throw new Error(`Failed to get download URL: ${error.message}`);
    }
  }

  async deleteFile(path: string): Promise<void> {
    try {
      const bucketId = this.getBucketId(path);
      const fileId = path.replace(/\//g, "_");
      await this.storage.deleteFile(bucketId, fileId);
      console.log(
        `✅ File deleted from Appwrite Storage (bucket: ${bucketId}): ${path}`,
      );
    } catch (error: any) {
      console.error(`❌ Appwrite deleteFile error (${path}):`, error);
      throw new Error(`Failed to delete file: ${error.message}`);
    }
  }

  async listFiles(path: string): Promise<string[]> {
    try {
      const bucketId = this.getBucketId(path);
      const response = await this.storage.listFiles(bucketId);
      const pathPrefix = path.replace(/\//g, "_");
      const matchingFiles = response.files
        .filter((file) => file.$id.startsWith(pathPrefix))
        .map((file) => file.$id.replace(/_/g, "/"));
      console.log(
        `✅ Listed ${matchingFiles.length} files in Appwrite Storage (bucket: ${bucketId}): ${path}`,
      );
      return matchingFiles;
    } catch (error: any) {
      console.error(`❌ Appwrite listFiles error (${path}):`, error);
      throw new Error(`Failed to list files: ${error.message}`);
    }
  }

  async fileExists(path: string): Promise<boolean> {
    try {
      const bucketId = this.getBucketId(path);

      // Special handling for lead-reports: files are stored as {campaignId}_{clientId}
      // Path format: lead-reports/869cp02nw_35974
      // File ID in Appwrite: 869cp02nw_35974 (NO .pdf extension!)
      let fileId: string;
      if (path.toLowerCase().startsWith('lead-reports/')) {
        // Extract just the ID part (NO extension - n8n creates them without .pdf)
        fileId = path.substring('lead-reports/'.length);
        console.log(`🔍 [DEBUG] Checking lead report:`, {
          path: path,
          bucketId: bucketId,
          fileId: fileId,
        });
      } else {
        // Default behavior: replace slashes with underscores
        fileId = path.replace(/\//g, "_");
      }

      console.log(`🔍 [DEBUG] Appwrite fileExists: checking bucket=${bucketId}, fileId=${fileId}`);
      await this.storage.getFile(bucketId, fileId);
      console.log(`✅ [DEBUG] File exists: ${fileId}`);
      return true;
    } catch (error: any) {
      if (error.code === 404) {
        console.log(`❌ [DEBUG] File not found (404): ${path}`);
        return false;
      }
      console.error(`❌ Appwrite fileExists error (${path}):`, error);
      throw new Error(`Failed to check file existence: ${error.message}`);
    }
  }

  /**
   * Check if a file exists by bucket ID and file ID directly.
   */
  async fileExistsInBucket(bucketId: string, fileId: string): Promise<boolean> {
    try {
      await this.storage.getFile(bucketId, fileId);
      return true;
    } catch (error: any) {
      if (error.code === 404) return false;
      console.error(`❌ fileExistsInBucket error (${bucketId}/${fileId}):`, error);
      return false;
    }
  }
}
